import React, { useEffect, useCallback, useRef, useState } from 'react';
import { useSelector, useDispatch } from "react-redux";
import { reset, isDirty } from 'redux-form';
import moment from 'moment';

import DiaryCreationForm from './diaryCreationForm';
import * as diaryActions from "../../Diaries/actions/index";
import { DEFAULT_TIME_FORMAT } from '../../../common/constants';
import AddFollowUp from "./addFollowUp";
import LoaderComponent from '../../../components/Loader';
import { hasClass, addClass, removeClass, toEpoch } from '../../../common/util';
import ErrorBoundary from "../../../components/AppError/errorBoundary";
import { fetchUsersListRequest } from '@/containers/AdminPanel/features/users/usersSlice';

// Helpers
const DEFAULT_DUE_DATE = () => moment().set({ hour: 23, minute: 59, second: 0 });

const resolveTags = (formData, wkNumber, customerLastName) => {
    const { system_tags, tags, CustomerLastName } = formData;

    if (system_tags?.length || tags?.length) {
        return (tags ?? []).map(tag => ({ id: tag, text: tag }));
    }

    const tagList = [];
    if (wkNumber) tagList.push(`#${wkNumber}`);

    const cln = customerLastName?.trim() || CustomerLastName;
    if (cln) {
        tagList.push(cln.length > 51 ? `${cln.substring(0, 47)}...` : cln);
    }

    return tagList.map(tag => ({ id: tag, text: tag }));
};

const resolveOwner = (formData, sessionContext, userId) => {
    if (formData.owner_id > 0) return formData.owner_id;
    if (sessionContext?.userView.id) return sessionContext.userView.id;
    return userId ?? 0;
};

const resolveDueDate = (dueDate, propsFormData) => {
    if (dueDate) return moment(dueDate);
    if (propsFormData.TodoId > 0) return moment(propsFormData.DueDate);
    return DEFAULT_DUE_DATE();
};

const buildFollowUpDates = (values) =>
    [values.followUpDueDate1, values.followUpDueDate2, values.followUpDueDate3]
        .filter(Boolean)
        .map(date => moment(date).format(DEFAULT_TIME_FORMAT));

// Custom Hooks 
const useScrollShadow = (selector) => {
    useEffect(() => {
        const handleScroll = () => {
            const header = document.querySelector(selector);
            if (!header) return;
            const atTop = header.getBoundingClientRect().top === 0;
            if (atTop && !hasClass(header, "shadow")) addClass(header, "shadow");
            if (!atTop && hasClass(header, "shadow")) removeClass(header, "shadow");
        };

        window.addEventListener('scroll', handleScroll, true);
        return () => window.removeEventListener('scroll', handleScroll, true);
    }, [selector]);
};

const DiaryCreation = (props) => {
    const {isEdit, referrer, wkId, actId, type, moduleId, isNew, onClose, wkNumber, customerLastName, dueDate } = props;    

    const dispatch = useDispatch();

    const { listingData, selectedDiary } = useSelector(state => state.diaries.listing);
    const diaryDetails = listingData?.data?.find(d => d.todo_id === selectedDiary) ?? null;

    const {
        isLoading, propsFormData, propsTags, userId,
        buttonLoading, sessionContext, formIsDirty,
    } = useSelector(state => ({
        isLoading:        state.diaryCreation.isLoading,
        propsFormData:    state.diaryCreation.formData,
        propsTags:        state.diaryCreation.tags,
        userId:           state.sessionContextOidc?.userView.id,
        buttonLoading:    state.workkardDetail.buttonLoaders.createDiary,
        sessionContext:   state.sessionContextOidc,
        formIsDirty:      isDirty('diaryCreation')(state),
    }));

    //Actions
    const createDiary         = useCallback((p) => dispatch(diaryActions.createDiary(p)),       [dispatch]);
    const updateDiary         = useCallback((p) => dispatch(diaryActions.updateDiary(p)),       [dispatch]);
    const resetDiaryFormData  = useCallback(() => dispatch(diaryActions.resetDiaryFormData()),  [dispatch]);
    const resetForm           = useCallback((f) => dispatch(reset(f)),                          [dispatch]);

    // Local state
    const pageRef = useRef("");
    const [currentPage, setCurrentPage] = useState("form");
    const [formData, setFormData] = useState({
        todo_id: null,
        priority: 'low',
        due_on_iso: DEFAULT_DUE_DATE(),
        enableReminder: false,
        file: [],
        tags: [],
    });

    // Custom hooks
    useScrollShadow(".modalHeader");

    // Sync edit form fields 
    useEffect(() => {
        if(isEdit) {
            const tags = resolveTags(diaryDetails, wkNumber, customerLastName);        
            const owner = resolveOwner(diaryDetails, sessionContext, userId);
            const effectiveDate = resolveDueDate(dueDate, diaryDetails);
            const daysDiff = effectiveDate.diff(moment().startOf("day"), "days");
            const priority = diaryDetails.priority || 'normal';
            let txtDays = null;

            const commonData = {
                owner,
                due_on_iso: effectiveDate,
                reminder_setting_tz: diaryDetails.reminder_date_tz,
                due_on_tz: diaryDetails.due_date_tz,
                priority: priority,
            };
        
            if (!isEdit) {
                setFormData(commonData);
                return;
            }

            if (daysDiff > 999) {
                txtDays = "999+";
            } else if (daysDiff > 0) {
                txtDays = daysDiff;
            }

            setFormData({
                ...commonData,
                todo_id: diaryDetails.todo_id,
                title: diaryDetails.title,
                description: diaryDetails.description,
                txtDays,
                reminder_setting_iso: diaryDetails.reminder_date
                    ? moment(diaryDetails.reminder_date)
                    : null,
                SystemTags: diaryDetails?.system_tags,
                enableReminder: Boolean(diaryDetails.reminder_date),
                tags,
                file: diaryDetails.TodoFile?.length
                    ? [{
                        guid: diaryDetails.FileGuid,
                        size: diaryDetails.FileSize,
                        name: diaryDetails.TodoFile,
                        url: diaryDetails.FileUrl,
                        uploadDate: diaryDetails.FileUploadOn,
                        caption: diaryDetails.caption,
                    }]
                    : [],
            });
        }
    }, []);

    useEffect(() => {
        dispatch(fetchUsersListRequest());
        dispatch(diaryActions.fetchDiaryTags())
    }, []);

    // Toggle Handlers
    const toggleFullView = useCallback((isFullView) => {
        const overlay = document.querySelector(".ReactModal__Overlay");
        const modal   = document.querySelector(".ReactModal__Content");
        if (isFullView) {
            addClass(overlay, "fullViewMain");
            addClass(modal, "fullView");
            removeClass(modal, "overflowNone");
        } else {
            removeClass(overlay, "fullViewMain");
            removeClass(modal, "fullView");
            addClass(modal, "overflowNone");
            if (pageRef.current === "followUp") addClass(modal, "modalAlignFromTop");
        }
    }, []);

    const closeForm = useCallback(() => {
        if (referrer === "gad" || referrer === "gwd") {
            window.location.href = `/gad?actId=${actId}&cId=${wkId}&#cd`;
            return;
        }
        if (isNew) { window.location.reload(); return; }

        resetDiaryFormData();
        resetForm('diaryCreation');
        onClose();
    }, [referrer, actId, wkId, isNew, resetDiaryFormData, resetForm, onClose]);

    const onSuccess = useCallback(() => {
        if (referrer === "views") { window.location.reload(); return; }
        closeForm();
    }, [referrer, closeForm]);

    const submitForm = useCallback((typeCase, values) => {
        if (buttonLoading) return;
        try {
            const tags = [
                ...(values.tags?.map(t => t.text) ?? []),
                ...(values.SystemTags ?? []),
            ];

            const file    = values.file?.[0];
            const caption = file?.caption?.toString().trim() ?? "";
            const guid    = file?.guid ?? "";
            
            const MODULE_ID_MAP = {
                activity: actId,
                workkard: wkId,
            };
            
            const payload = {
                title: values.title.toString().trim(),
                description: values.description?.trim().slice(3, -4) || "",
                due_on: toEpoch(values.due_on_iso) ?? 0,
                due_on_tz: values.due_on_tz || "",
                reminder_setting: values.enableReminder ? toEpoch(values.reminder_setting_iso) : 0,
                reminder_setting_tz: values.enableReminder ? values.reminder_setting_tz : "",
                priority: values.priority,
                tags,
                assignee_id: values.owner,
                comment: values.isDateComment && values.dateChangeComment ? values.dateChangeComment.toString().trim() : "",
                attachments: guid ? [{ guid, caption }] : [],
                module_id: propsFormData?.ModuleId ?? MODULE_ID_MAP[type] ?? 0,
                module_type: propsFormData?.Module || type,
                callback: onSuccess,
                referrer,
            };
            
            switch (typeCase) {
                case "addFollowUp":
                    setCurrentPage("followUp");
                    pageRef.current = "followUp";
                    toggleFullView(false);
                    return;

                case "save": {
                    const followUpDueDates = buildFollowUpDates(values);
                    if (followUpDueDates.length) payload.DueDatePopup = JSON.stringify(followUpDueDates);
                    createDiary(payload);
                    return;
                }

                case "update":
                    payload.id = formData.todo_id;
                    updateDiary(payload);
                    return;
            }
        } catch (error) {
            console.error("Error during diary submission:", error);
        }
    }, [buttonLoading, propsFormData, moduleId, type, actId, wkId, formData.todo_id, onSuccess, referrer, toggleFullView, createDiary, updateDiary]);

    const closeFollowUp = useCallback(() => {
        setCurrentPage("form");
        toggleFullView(true);
    }, [toggleFullView]);
    
    return (
        <ErrorBoundary>
            {isLoading && <LoaderComponent />}
            <DiaryCreationForm
                onSubmit={submitForm}
                initialValues={formData}
                tags={propsTags}
                isEdit={isEdit}
                toggleFullView={toggleFullView}
                closeForm={closeForm}
                isDirty={formIsDirty}
                page={pageRef.current}
                userId={userId}
                currentPage={currentPage}
                systemTags={propsFormData.SystemTags}
                buttonLoading={buttonLoading}
            />
            {currentPage === "followUp" && (
                <AddFollowUp
                    onSubmit={submitForm}
                    onClose={closeFollowUp}
                    buttonLoading={buttonLoading}
                />
            )}
        </ErrorBoundary>
    );
};

export default DiaryCreation;